/**
 * Pure helpers for the changeSite (Task 13) store-switch-keeps-cart flow.
 *
 * POST /:orgId/:siteId/orders/:orderId/changeSite  body { newSiteId }
 * Response: { newOrder: OrderAndLines, droppedItems: [{productCode, name, reason}] }
 */

import { ResponseOrderWithLines } from "~/entities/orderEntities";

export interface DroppedItem {
  productCode: string;
  name: string;
  reason: string;
}

export interface ChangeSiteResponse {
  newOrder: ResponseOrderWithLines;
  droppedItems: DroppedItem[];
}

/**
 * Decide which site the current order must be migrated FROM (via changeSite)
 * before it can be shown at `currentSiteId` — or `null` when no migration is
 * needed (no order, unknown home site, or the order already lives here).
 *
 * The home site MUST be the order's own tracked site (`orderSiteId` in the
 * order session), NOT the UI "previous site". `prevSite` means "the last store
 * the user touched" and drifts out of sync with where the order actually lives,
 * so using it to gate changeSite let a store-switch round-trip (A→B→A) skip the
 * migration and strand the order under the wrong site → silently empty cart.
 * Keying off the order's real home makes that impossible.
 */
export function changeSiteSource(opts: {
  orderId?: string;
  orderSiteId?: string;
  currentSiteId: string;
}): string | null {
  const { orderId, orderSiteId, currentSiteId } = opts;
  if (!orderId || !orderSiteId) return null;
  if (orderSiteId === currentSiteId) return null;
  return orderSiteId;
}

/**
 * Map a backend reason code to a short, human-friendly phrase explaining why
 * an item was dropped during a store switch.
 *
 * Unknown codes fall back to a generic "unavailable" so the toast never leaks
 * a raw code or shows an empty parenthetical.
 */
const reasonToPhrase = (reason: string): string => {
  switch (reason) {
    case "SOLD_OUT":
      return "sold out";
    case "NOT_AVAILABLE_AT_SITE":
      return "not on this store's menu";
    default:
      return "unavailable";
  }
};

/**
 * Build a human-readable toast message summarising which items were dropped
 * when switching stores AND why, grouping items by reason.
 *
 * e.g. "Removed: Big Burger (sold out), Fries (not on this store's menu)".
 *
 * Returns null when no items were dropped (no toast needed).
 */
export const buildDroppedItemsToast = (
  droppedItems: DroppedItem[],
): string | null => {
  if (!droppedItems.length) return null;

  // Group item names by reason phrase, preserving first-seen order.
  const groups = new Map<string, string[]>();
  for (const item of droppedItems) {
    const phrase = reasonToPhrase(item.reason);
    const existing = groups.get(phrase);
    if (existing) {
      existing.push(item.name);
    } else {
      groups.set(phrase, [item.name]);
    }
  }

  const segments = Array.from(groups.entries()).map(
    ([phrase, names]) => `${names.join(", ")} (${phrase})`,
  );

  return `Removed: ${segments.join(", ")}. Prices have been updated.`;
};
